import React, { useMemo } from "react";
import { Stack, Text } from "@mantine/core";

import { Display, formatDateTime } from "@/utils";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  StatusBadge,
  RecordActions,
} from "@/components/generic";

export interface DisplaysTableProps {
  /** Array of displays to display */
  displays: Display[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditDisplay?: (display: Display) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateDisplay?: (display: Display) => void;

  /** Callback when remove action is clicked */
  onRemoveDisplay?: (display: Display) => void;

  /** Callback when view action is clicked */
  onViewDisplay?: (display: Display) => void;

  /** Callback when go in action is clicked */
  onGoInDisplay?: (display: Display) => void;

  /** ID of display currently being removed */
  removingDisplayId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const DisplaysTable: React.FC<DisplaysTableProps> = ({
  displays,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditDisplay,
  onDuplicateDisplay,
  onRemoveDisplay,
  onViewDisplay,
  onGoInDisplay,
  removingDisplayId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Display>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Display Name",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Unnamed Display"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "layout",
        label: "Layout/Theme",
        sortable: true,
        width: 240,
        minWidth: 240,
        align: "left",
        hideOnMobile: true,
        render: (_, record) => {
          return (
            <Stack gap={0}>
              <Text>{record.layout?.name || "N/A"}</Text>
              <Text>{record.theme?.name || "N/A"}</Text>
            </Stack>
          );
        },
      },
      {
        key: "emergency",
        label: "Emergency Block",
        sortable: true,
        width: 240,
        minWidth: 240,
        align: "left",
        hideOnMobile: true,
        render: (_, record) => {
          return <Text>{record.emergencyContentBlock?.name || "--"}</Text>;
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        hideOnMobile: true,
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },

      {
        key: "updatedAt",
        label: "Updated",
        sortable: true,
        width: 120,
        minWidth: 120,
        render: (_, record) => (
          <Text size="sm" c="dimmed">
            {formatDateTime(record.updatedAt || record.createdAt, "datetime")}
          </Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 280,
        minWidth: 280,
        align: "left",
        render: (_, record) => (
          <RecordActions
            context={record}
            onViewClick={onViewDisplay}
            onEditClick={onEditDisplay}
            onDuplicateClick={onDuplicateDisplay}
            onRemoveClick={onRemoveDisplay}
            onGoInClick={onGoInDisplay}
            removeInProgress={removingDisplayId === record.id}
            viewTooltip="Preview display"
            goInTooltip="View a list of connected devices"
          />
        ),
      },
    ],
    [onViewDisplay, onEditDisplay, onRemoveDisplay, removingDisplayId]
  );

  return (
    <AppTable
      data={displays}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(display) => display?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
